import React, { useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { MapPin, Calendar, Check, ArrowRight, Sparkles, User, Users, HeartHandshake, Star, Award, Phone } from 'lucide-react';
import { translations, pujaPackages } from '../mock';
import { useLang } from '../context/LanguageContext';
import { useData } from '../context/DataContext';

const iconMap = { User, Users, HeartHandshake };

export default function PujaDetail() {
  const { slug } = useParams();
  const { t, lang } = useLang();
  const navigate = useNavigate();
  const { pujas, pandits } = useData();
  const puja = pujas.find((p) => p.slug === slug);
  const assignedPandit = puja && puja.panditId ? pandits.find((pd) => pd.id === puja.panditId) : null;
  const effectivePackages = (puja && puja.packages && puja.packages.length) ? puja.packages : pujaPackages;
  const [selectedPkg, setSelectedPkg] = useState(effectivePackages[Math.min(2, effectivePackages.length - 1)]?.id || effectivePackages[0]?.id);

  if (!puja) return <div className="text-center py-20">Puja not found</div>;

  const proceed = () => {
    navigate(`/participate/${puja.slug}?package=${selectedPkg}`);
  };

  return (
    <div className="max-w-6xl mx-auto px-6 py-10">
      <Link to="/upcoming-pujas" className="text-orange-700 text-sm hover:underline">
        ← {lang === 'en' ? 'Back to Pujas' : 'पूजाओं में वापस'}
      </Link>

      <div className="grid lg:grid-cols-2 gap-10 mt-4">
        <div className="rounded-2xl overflow-hidden shadow-xl h-fit sticky top-24">
          <img src={puja.image} alt="" className="w-full h-[400px] object-cover" />
        </div>
        <div>
          <h1 className="text-3xl md:text-4xl font-bold text-stone-900 leading-tight mb-4">
            {lang === 'en' ? puja.title_en : puja.title_hi}
          </h1>
          <div className="flex flex-wrap gap-3 mb-5">
            <div className="bg-amber-50 border border-amber-200 rounded-full px-4 py-1.5 text-sm flex items-center gap-2">
              <MapPin className="w-4 h-4 text-amber-700" />
              {lang === 'en' ? puja.temple_en : puja.temple_hi}
            </div>
            <div className="bg-blue-50 border border-blue-200 rounded-full px-4 py-1.5 text-sm flex items-center gap-2">
              <Calendar className="w-4 h-4 text-blue-700" />
              {lang === 'en' ? puja.date_label_en : puja.date_label_hi}
            </div>
          </div>
          <p className="text-stone-700 leading-relaxed mb-6">
            {lang === 'en' ? puja.description_en : puja.description_hi}
          </p>

          <div className="bg-gradient-to-br from-orange-50 to-amber-50 border border-orange-200 rounded-2xl p-5 mb-6">
            <h3 className="font-bold text-orange-800 mb-3 flex items-center gap-2">
              <Sparkles className="w-4 h-4" /> {t(translations.puja.benefits)}
            </h3>
            <ul className="grid grid-cols-2 gap-2 text-sm">
              {(lang === 'en' ? puja.benefits_en : puja.benefits_hi).map((b, i) => (
                <li key={i} className="flex items-center gap-2 text-stone-700">
                  <Check className="w-4 h-4 text-green-600 shrink-0" /> {b}
                </li>
              ))}
            </ul>
          </div>

          {/* Assigned Pandit Ji card */}
          {assignedPandit && (
            <div className="bg-white border-2 border-orange-200 rounded-2xl p-4 mb-6 flex items-center gap-4 shadow-sm">
              {assignedPandit.photo ? (
                <img src={assignedPandit.photo} alt={assignedPandit.name_en} className="w-16 h-16 rounded-full object-cover border-2 border-orange-300 shrink-0" />
              ) : (
                <div className="w-16 h-16 rounded-full bg-gradient-to-br from-orange-400 to-red-500 flex items-center justify-center text-white text-2xl shrink-0">🙏</div>
              )}
              <div className="flex-1 min-w-0">
                <div className="text-[10px] uppercase tracking-wider text-orange-700 font-bold">{lang === 'en' ? 'Puja will be performed by' : 'पूजा संपन्न कराने वाले'}</div>
                <div className="font-bold text-stone-900 text-sm md:text-base truncate">{lang === 'en' ? assignedPandit.name_en : assignedPandit.name_hi}</div>
                <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-stone-600 mt-1">
                  <span className="flex items-center gap-1"><Award className="w-3 h-3 text-orange-600" /> {assignedPandit.experience}+ yrs</span>
                  <span className="flex items-center gap-1 truncate"><MapPin className="w-3 h-3 text-orange-600" /> {lang === 'en' ? assignedPandit.temple_en : assignedPandit.temple_hi}</span>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Packages section */}
      <div className="mt-12">
        <div className="section-heading">
          <span className="text-2xl md:text-3xl font-bold gradient-text">
            {lang === 'en' ? `${effectivePackages.length} Ways to Participate` : `भाग लेने के ${effectivePackages.length} तरीके`}
          </span>
        </div>

        <div className="grid sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
          {effectivePackages.map((pkg) => {
            const Icon = iconMap[pkg.icon] || User;
            const isSel = selectedPkg === pkg.id;
            return (
              <button
                key={pkg.id}
                onClick={() => setSelectedPkg(pkg.id)}
                className={`relative text-left rounded-2xl border-2 p-5 transition-all bg-white ${
                  isSel
                    ? 'border-orange-500 shadow-lg -translate-y-1 ring-4 ring-orange-100'
                    : 'border-orange-100 hover:border-orange-300 hover:-translate-y-0.5'
                }`}
              >
                {pkg.popular && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-gradient-to-r from-orange-500 to-red-500 text-white text-xs font-bold px-3 py-1 rounded-full flex items-center gap-1 whitespace-nowrap">
                    <Star className="w-3 h-3 fill-white" /> {lang === 'en' ? 'Most Popular' : 'सर्वस्वीकृत'}
                  </div>
                )}
                <div className={`w-14 h-14 rounded-2xl mb-3 flex items-center justify-center ${isSel ? 'bg-gradient-to-br from-orange-500 to-red-500 text-white' : 'bg-orange-100 text-orange-700'}`}>
                  <Icon className="w-6 h-6" />
                </div>
                <h3 className="font-bold text-stone-900">{lang === 'en' ? pkg.name_en : pkg.name_hi}</h3>
                <div className="text-2xl font-bold text-orange-700 my-2">₹ {pkg.price}</div>
                <p className="text-xs text-stone-600 mb-3">{lang === 'en' ? pkg.desc_en : pkg.desc_hi}</p>
                <ul className="space-y-1 text-xs text-stone-600">
                  {((lang === 'en' ? pkg.features_en : (pkg.features_hi && pkg.features_hi.length ? pkg.features_hi : pkg.features_en)) || []).slice(0, 3).map((f, i) => (
                    <li key={i} className="flex items-start gap-1.5"><Check className="w-3 h-3 text-green-600 mt-0.5 shrink-0" /> {f}</li>
                  ))}
                </ul>
                {isSel && (
                  <div className="absolute top-3 right-3 w-6 h-6 rounded-full bg-orange-500 text-white flex items-center justify-center">
                    <Check className="w-4 h-4" />
                  </div>
                )}
              </button>
            );
          })}
        </div>

        <div className="mt-8 flex justify-center">
          <button onClick={proceed} className="btn-orange rounded-full px-10 py-4 font-semibold flex items-center gap-2 text-lg shadow-lg">
            {lang === 'en' ? 'Continue with' : 'चुनें'}{' '}
            {lang === 'en'
              ? effectivePackages.find((p) => p.id === selectedPkg)?.name_en
              : effectivePackages.find((p) => p.id === selectedPkg)?.name_hi}
            <ArrowRight className="w-5 h-5" />
          </button>
        </div>
      </div>
    </div>
  );
}
